import type {
  ChildConfig,
  RuleExpression as ConfigRuleExpression,
  FieldChild,
  GroupConfig,
  HelpTextChild,
  RuleConfig,
  SchemaConfig,
} from "./generator/config/config";
import {
  assert,
  type Child,
  type Document,
  type Field,
  type Group,
  type RuleExpression as ProjectRuleExpression,
  type Schema,
} from "./project";

export function compileConfig(document: Document): SchemaConfig {
  assert(document.type === "schema");
  const schema = document.schema;

  const children: ChildConfig[] = [];
  for (const child of schema.children) {
    children.push(compileChild(child, document));
  }

  return {
    id: schema.fimId,
    title: schema.basicData.name,
    children,
    rules: compileRules(schema, document),
  };
}

function compileRules(
  element: Schema | Group | Field,
  document: Document,
): RuleConfig[] {
  const ruleConfigs: RuleConfig[] = [];
  for (const ruleRef of element.rules) {
    const ruleConfig = compileRule(ruleRef.fimId, document);
    if (ruleConfig !== null) {
      ruleConfigs.push(ruleConfig);
    }
  }

  return ruleConfigs;
}

export function compileField(
  field: Field,
  required: boolean,
  document: Document,
): FieldChild | HelpTextChild {
  const id = field.fimId;
  const label = field.basicData.name;
  const type = field.inputType;

  if (type === "input") {
    // TODO: add constraints
    return {
      type: "field",
      config: {
        id,
        type,
        label,
        required,
        rules: compileRules(field, document),
      },
    };
  } else if (type === "select") {
    return {
      type: "field",
      config: {
        id,
        type,
        label,
        required,
        options: field.options,
        rules: compileRules(field, document),
      },
    };
  } else if (type === "label") {
    return {
      type: "help",
      config: {
        id,
        type,
        label,
        value: field.value || "",
      },
    };
  } else {
    throw new Error(`Unsupported field type: ${type}`);
  }
}

function compileGroup(group: Group, document: Document): GroupConfig {
  const id = group.fimId;
  const title = group.basicData.name;
  const required = true;

  const children: ChildConfig[] = [];
  for (const child of group.children) {
    children.push(compileChild(child, document));
  }

  return {
    id,
    title,
    required,
    children,
    rules: compileRules(group, document),
  };
}

function compileChild(config: Child, document: Document): ChildConfig {
  switch (config.type) {
    case "group": {
      const group = document.groups[config.element.fimId];

      return {
        type: "group",
        config: compileGroup(group, document),
      };
    }
    case "field": {
      const field = document.fields[config.element.fimId];
      return compileField(field, true, document);
    }
  }
}

function compileRule(ruleId: string, document: Document): RuleConfig | null {
  const rule = document.rules[ruleId];
  if (rule.ruleType === "valid") {
    // TODO: handle validity rules
    console.warn(`Skip rule ${ruleId}: validity rules not supported yet`);
    return null;
  }

  if (rule.target === undefined) {
    console.warn(`Skip rule ${ruleId}: target is undefined`);
    return null;
  }

  try {
    const expression = mapRuleExpression(rule.expression || null);
    const targetId =
      rule.target !== undefined
        ? rule.target.map((id) => id.fimId).join(".")
        : "";

    return {
      id: ruleId,
      type: "enable",
      targetId,
      expression,
    };
  } catch (e) {
    console.warn(`Cannot compile rule ${ruleId}:`, e);
    return null;
  }
}

function mapRuleExpression(
  expression: ProjectRuleExpression | null,
): ConfigRuleExpression {
  if (expression === null) {
    throw "Cannot compile empty expression";
  }

  if (typeof expression === "string" || typeof expression === "boolean") {
    return expression;
  }

  if (expression.type === "identifier") {
    if (expression.identifier === null) {
      throw "Cannot compile identifier with undefined identifier";
    }

    return {
      type: "identifier",
      identifier: expression.identifier,
    };
  } else if (expression.type === "operation") {
    return {
      type: "operation",
      operation: expression.operation,
      operands: expression.operands.map(mapRuleExpression),
    };
  } else {
    throw `Unknown expression type ${expression}`;
  }
}
